import { useState } from "react";
import { toast } from "sonner";
import { Search, Link2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatError } from "@/lib/api";
import { ROLES } from "@/lib/format";
import { Card, Sel } from "@/components/Field";
import { StatusBadge } from "@/components/StatusBadge";

const roleOpts = Object.entries(ROLES).map(([value, label]) => ({ value, label }));

const LinkForm = ({ u, associations, onDone }) => {
  const free = associations.filter((a) => !u.memberships.some((m) => m.association_id === a.id && m.status === "active"));
  const [aid, setAid] = useState("");
  const [role, setRole] = useState("member");
  if (!free.length) return null;
  const link = async () => {
    try { await api.post("/platform/memberships", { user_id: u.id, association_id: aid, role }); toast.success("Collegamento creato"); setAid(""); onDone(); } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="w-56"><Sel testId={`link-assoc-${u.id}`} value={aid} onChange={setAid} placeholder="Collega a…" options={free.map((a) => ({ value: a.id, label: a.name }))} /></div>
      <div className="w-44"><Sel testId={`link-role-${u.id}`} value={role} onChange={setRole} options={roleOpts} /></div>
      <Button data-testid={`link-submit-${u.id}`} className="h-11" disabled={!aid} onClick={link}><Link2 className="h-4 w-4 mr-1" /> Collega</Button>
    </div>
  );
};

const UserRow = ({ u, associations, onDone }) => {
  const unlink = async (m) => {
    if (!window.confirm(`Scollegare ${u.name || u.username} da ${m.association_name}?`)) return;
    try { await api.delete(`/platform/memberships/${m.membership_id}`); toast.success("Collegamento rimosso"); onDone(); } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <li data-testid={`platform-user-${u.id}`} className="px-5 py-4 space-y-3">
      <div>
        <div className="font-semibold text-slate-900">{u.name || u.username}</div>
        <div className="text-sm text-slate-500">{[u.username && `@${u.username}`, u.email].filter(Boolean).join(" · ")}</div>
      </div>
      <div className="flex flex-wrap gap-2">
        {u.memberships.length === 0 && <span className="text-sm text-slate-500">Nessuna associazione</span>}
        {u.memberships.map((m) => (
          <span key={m.membership_id} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 pl-3 pr-1 py-1 text-sm">
            {m.association_name} · {ROLES[m.role]}{m.status !== "active" && <StatusBadge label="Disattivato" />}
            <button data-testid={`unlink-${m.membership_id}`} onClick={() => unlink(m)} className="h-7 w-7 inline-flex items-center justify-center rounded hover:bg-slate-200 text-red-700" aria-label="Scollega"><X className="h-4 w-4" /></button>
          </span>
        ))}
      </div>
      <LinkForm u={u} associations={associations} onDone={onDone} />
    </li>
  );
};

export const PlatformUsers = ({ associations, onChanged }) => {
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);
  const search = async (e) => {
    e?.preventDefault();
    try { setItems((await api.get("/platform/users", { params: { q } })).data); } catch (err) { toast.error(formatError(err)); }
  };
  return (
    <Card testId="platform-users">
      <div className="px-5 py-4 border-b border-slate-200 space-y-3">
        <div>
          <h2 className="font-heading text-lg font-semibold text-slate-900">Utenti e associazioni</h2>
          <p className="text-sm text-slate-500">Collega la stessa persona a più associazioni: con un unico accesso sceglierà quale aprire.</p>
        </div>
        <form onSubmit={search} className="flex gap-2 max-w-lg">
          <Input data-testid="platform-user-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nome, nome utente o e-mail" className="h-11" />
          <Button data-testid="platform-user-search-btn" type="submit" className="h-11" disabled={q.trim().length < 2}><Search className="h-4 w-4 mr-1" /> Cerca</Button>
        </form>
      </div>
      {items && (items.length === 0 ? <p data-testid="platform-users-empty" className="px-5 py-6 text-sm text-slate-500">Nessun utente trovato.</p> : (
        <ul className="divide-y">{items.map((u) => <UserRow key={u.id} u={u} associations={associations} onDone={() => { search(); onChanged?.(); }} />)}</ul>
      ))}
    </Card>
  );
};
